import type { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import type { LineString } from 'geojson';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import authApi, { type AuthApiResponse } from '@api/auth/auth';
import driversApi, { type SupplierResponse } from '@api/drivers/drivers';
import feedbacksApi, { type CreateFeedbackApiResponse } from '@api/feedbacks/feedbacks';
import { default as locationsApi, type LocationsApiResponse } from '@api/locations/locations';
import reservationsApi, { type ReservationApiResponse } from '@api/reservations/reservations';
import tripsApi, { type TripApiResponse, type TripsApiResponse } from '@api/trips/trips';
import vehiclesApi from '@api/vehicles/vehicles';
import { EFeedbacks, ELocations } from '@enums/api.enum';
import { EMap } from '@enums/map.enum';
import { ERoutes } from '@enums/routes.enum';
import { EStyles } from '@enums/style.enum';
import { TimeFormat } from '@enums/timeFormat.enum';
import { TripStatus } from '@enums/trips.enum';
import { setError, setIsLoading } from '@store/auth/authSlice';
import type { RootState } from '@store/store';
import {
	addDriverToTrip,
	addDriverTrackingUrl,
	addVehicleToTrip,
	resetTripsState,
	setCurrentTrip,
	setOrUpdateRateToTrip,
	setTrips,
	updateDriverVehicleTrip,
	updateTrip,
	type Driver,
	type LatLng,
	type Rate,
	type Reservation,
	type Trip,
	type Vehicle,
} from '@store/trips/tripsSlice';
import { handleGetLocationFromPoint } from '@utils/getLocationFromPoint';

type DriverVehicleParams = {
	id: Trip['id'];
	driverId?: Driver['id'] | null;
	vehicleId?: Vehicle['id'] | null;
};

const defaultFilters = {
	status: [TripStatus.InRoute, TripStatus.New, TripStatus.Done],
	fromDate: dayjs().subtract(3, TimeFormat.Hour).unix(),
	toDate: dayjs().add(3, TimeFormat.Hour).unix(),
	limit: 100,
};

export default function useApi() {
	const trips = useSelector((state: RootState) => state.trips.data);
	const flightNumber = useSelector((state: RootState) => state.trips.flightNumber);
	const currentTrip = useSelector((state: RootState) => state.trips.currentTrip);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const resetIfCurrentTripStale = () => {
		if (currentTrip?.date && dayjs.unix(currentTrip.date).isBefore(dayjs().subtract(4, TimeFormat.Hour))) {
			dispatch(resetTripsState());
		}
	};

	const handleNavigateByTripsLength = (data: Trip[], id?: Trip['id']) => {
		if (data.length === 1 && id) {
			getSingleTrip(id, data);
			navigate(`${ERoutes.tripInfo}/${id}`);
		} else if (data.length === 0 || data.length > 1) {
			navigate(ERoutes.trips);
		}
	};

	const getAccessToken = async (code?: string): Promise<string | undefined> => {
		if (!code) {
			return;
		}
		resetIfCurrentTripStale();

		const result: AxiosResponse<AuthApiResponse> = await authApi.get({ code });

		return result?.data?.token;
	};

	const getLocations = async (): Promise<LocationsApiResponse['records'] | undefined> => {
		const result: AxiosResponse<LocationsApiResponse> = await locationsApi.get({ type: ELocations.airportType });

		if (result.data) {
			return result.data.records;
		}
	};

	const getTrips = async (flightNumber: string) => {
		if (!flightNumber) {
			dispatch(setIsLoading(false));
			navigate(ERoutes.search);
		} else {
			resetIfCurrentTripStale();
			const locations = await getLocations();
			const result: AxiosResponse<TripsApiResponse> = await tripsApi.get({
				...defaultFilters,
				flightNumber: [flightNumber],
			});
			dispatch(setIsLoading(false));
			if (result.data) {
				const tripsWithLocations = result.data.records.map((trip) => ({
					...trip,
					from: handleGetLocationFromPoint(trip.from, locations || []),
					to: handleGetLocationFromPoint(trip.to, locations || []),
					stops: trip.stops?.map((stop) => ({
						...stop,
						address: handleGetLocationFromPoint(stop.address, locations || []),
					})),
				}));
				dispatch(setTrips({ records: tripsWithLocations, flightNumber }));
				const id = result.data.records[0]?.id;
				handleNavigateByTripsLength(tripsWithLocations, id);
			}
		}
	};

	const getTripsByVehicleId = async (vehicleId: number) => {
		if (!vehicleId) {
			dispatch(setIsLoading(false));
			navigate(ERoutes.search);
		} else {
			resetIfCurrentTripStale();
			dispatch(setIsLoading(true));
			const result: AxiosResponse<TripsApiResponse> = await tripsApi.get({
				...defaultFilters,
				status: [TripStatus.InRoute],
				vehicles: [vehicleId],
			});
			dispatch(setIsLoading(false));

			if (result.data) {
				const tripData = result.data.records[0];
				dispatch(setCurrentTrip(tripData));

				if (tripData?.flightNumber) {
					dispatch(setOrUpdateRateToTrip({ id: tripData.id as number, rate: { flightNumber: tripData.flightNumber } }));
				}
			}
		}
	};

	const getSingleTrip = async (id: Trip['id'], data?: Trip[]) => {
		resetIfCurrentTripStale();
		const result: AxiosResponse<TripApiResponse> = await tripsApi.getSingle({ id });

		if (result.data) {
			getSingleReservation(result.data.reservations?.[0]?.id || 0);
			const selectedIndex = result.data.mapRoutes?.selectedIndex || 0;
			const coords = result.data.mapRoutes?.options[selectedIndex]?.coordinates.map((coord: LatLng) => [coord.lng, coord.lat]);

			if (coords && coords.length > 0) {
				const activeTrips = data || trips;
				const index = activeTrips.findIndex((trip) => trip.id === id);

				dispatch(setIsLoading(false));
				const route = { type: EMap.LineString, coordinates: coords || [] } as LineString;
				if (index > -1) {
					dispatch(setCurrentTrip({ ...activeTrips[index], route }));
				} else {
					const { mapRoutes, ...tripData } = result.data;
					dispatch(setCurrentTrip({ ...tripData, route }));
				}
				getSingleDriver(result.data.driver?.id);
				getSingleVehicle(result.data.vehicle?.id);
			}
		}
	};

	const getSingleReservation = async (id: Reservation['id']) => {
		const result: AxiosResponse<ReservationApiResponse> = await reservationsApi.getSingle({ id });

		if (result.data) {
			const driverTrackingUrl = result.data.metadata?.icabbiTrackingUrl;

			if (driverTrackingUrl) {
				dispatch(addDriverTrackingUrl(driverTrackingUrl));
			}
		}
	};

	const getSingleDriver = async (id?: Driver['id']) => {
		if (!id) {
			return;
		}
		const suppliers: AxiosResponse<SupplierResponse> = await driversApi.getSuppliers();
		const result: AxiosResponse<Driver & { displayName: string }> = await driversApi.getSingle({ id });

		if (result.data) {
			const company: string[] = [];
			if (suppliers.data?.list) {
				suppliers.data.list.forEach((supplier) => {
					if (result.data?.suppliersIds?.includes(supplier.id)) {
						company.push(supplier.label);
					}
				});
			}
			dispatch(addDriverToTrip({ ...result.data, company: company.join(EStyles.commaSpace), name: result.data.displayName }));
		}
	};

	const getSingleVehicle = async (id?: Vehicle['id']) => {
		if (!id) {
			return;
		}
		const result: AxiosResponse<Vehicle> = await vehiclesApi.getSingle({ id });
		if (result.data) {
			dispatch(addVehicleToTrip(result.data));
		}
	};

	const getDriverAndVehicle = async ({ id, driverId, vehicleId }: DriverVehicleParams) => {
		if (!id) {
			return;
		}

		let driverData: Driver | undefined;
		let vehicleData: Vehicle | undefined;
		if (driverId) {
			const suppliers: AxiosResponse<SupplierResponse> = await driversApi.getSuppliers();
			const result: AxiosResponse<Driver & { displayName: string }> = await driversApi.getSingle({ id: driverId });
			if (result.data) {
				const company: string[] = [];

				if (suppliers.data?.list) {
					suppliers.data.list.forEach((supplier) => {
						if (result.data?.suppliersIds?.includes(supplier.id)) {
							company.push(supplier.label);
						}
					});
				}
				driverData = { ...result.data, company: company.join(EStyles.commaSpace), name: result.data.displayName, id: driverId };
				if (id === currentTrip?.id) {
					dispatch(addDriverToTrip(driverData));
				}
			}
		}

		const currentVehicleId = vehicleId || driverData?.currentVehicleId;
		if (currentVehicleId) {
			const vehicleResult: AxiosResponse<Vehicle> = await vehiclesApi.getSingle({ id: currentVehicleId });
			if (vehicleResult.data) {
				if (id === currentTrip?.id) {
					dispatch(addVehicleToTrip(vehicleResult.data));
				}
				vehicleData = { ...vehicleResult.data, id: currentVehicleId };
			}
			dispatch(updateDriverVehicleTrip({ driver: driverData, vehicle: vehicleData, id }));
		}
	};

	const updateSingleTrip = async (id: Trip['id']) => {
		if (!id || !flightNumber) {
			return;
		}
		const result: AxiosResponse<TripsApiResponse> = await tripsApi.get({
			...defaultFilters,
			flightNumber: [flightNumber],
		});
		if (result.data) {
			const trip: Trip | undefined = result.data.records.find((trip: Trip) => trip.id === id);
			if (!trip) {
				const locations = await getLocations();
				const tripsWithLocations = result.data.records.map((trip: Trip) => ({
					...trip,
					from: handleGetLocationFromPoint(trip.from, locations || []),
					to: handleGetLocationFromPoint(trip.to, locations || []),
					stops: trip.stops?.map((stop: Trip['stops'][number]) => ({
						...stop,
						address: handleGetLocationFromPoint(stop.address, locations || []),
					})),
				}));
				dispatch(setTrips({ records: tripsWithLocations, flightNumber }));
			} else {
				dispatch(updateTrip(trip));
				if (id === currentTrip?.id) {
					getSingleTrip(id);
				}
			}
		}
	};

	const createFeedback = async (values: Omit<Rate, 'id'>, tripId: Trip['id'], driverId?: Driver['id'], isQRMode: boolean = false) => {
		dispatch(setIsLoading(true));
		const result: AxiosResponse<CreateFeedbackApiResponse> = await feedbacksApi.createFeedback({
			...values,
			source: isQRMode ? EFeedbacks.QR : EFeedbacks.CrewLink,
			tripId,
			driverId,
		});
		dispatch(setIsLoading(false));

		if (result.data) {
			const id = result.data.id;
			setError(undefined);
			if (isQRMode) {
				dispatch(resetTripsState());
			} else {
				dispatch(setOrUpdateRateToTrip({ id: tripId as number, rate: { ...values, id } }));
			}
			navigate(ERoutes.thankYou);
		} else {
			dispatch(setError(t('rateTrip.form.error')));
		}
	};

	return {
		getAccessToken,
		getTrips,
		getSingleTrip,
		getSingleReservation,
		getDriverAndVehicle,
		updateSingleTrip,
		createFeedback,
		getTripsByVehicleId,
	};
}
